/**
 * use-assign-job — PATCH /v1/jobs/:id/assignment (Wave 3 Part 2 / Quick 260615-uf9).
 *
 * Backs the drag-drop dispatch board. Because the moved job id is only known at
 * drop time, this is a single mutation whose path is built in the mutationFn from
 * the variables (jobId), rather than a per-id useApiMutation.
 *
 * Quick 260616-e14 (#2) — OPTIMISTIC: on drop we immediately patch the cached jobs
 * list (move the job to the target driver + position) so the board updates with no
 * perceptible delay; the PATCH fires in the background. On error we roll back to the
 * snapshot; on settle we invalidate so the server's authoritative dense sort order
 * reconciles. We do NOT await the response before the UI moves.
 */

import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { apiErrorFromResponse, authedFetch, useApiOpts } from '@lodex-pro/web-shared';
import type { DispatchJob } from '../jobs/use-jobs';

export interface AssignVars {
  jobId: string;
  assignedDriverId: string | null;
  beforeJobId: string | null;
}

export interface AssignResult {
  job: { id: string; assignedDriverId: string | null; dispatcherSortOrder: number | null };
}

interface JobsCache {
  readonly jobs: readonly DispatchJob[];
}

interface MutationContext {
  snapshot: Array<[readonly unknown[], JobsCache | undefined]>;
}

/**
 * Only a cache holding a jobs array can be patched. Other queries share the ['jobs']
 * key prefix: #332's COMPLETED TODAY count is ['jobs', { board: 'completed-count' }]
 * with data `{ count }`. Patching that threw, which aborted every drag before the
 * PATCH was sent (ClickUp 86bc8a0ec).
 */
function isJobsCache(data: unknown): data is JobsCache {
  return (
    typeof data === 'object' && data !== null && Array.isArray((data as { jobs?: unknown }).jobs)
  );
}

/** Patch one cached jobs list: move `vars.jobId` to the target driver + position. */
function applyOptimistic(cache: JobsCache, vars: AssignVars): JobsCache {
  const moved = cache.jobs.find((j) => j.id === vars.jobId);
  if (moved === undefined) return cache;
  // Position: insert just before the anchor job, else append to the end of the column.
  let newOrder: number;
  if (vars.beforeJobId !== null) {
    const target = cache.jobs.find((j) => j.id === vars.beforeJobId);
    newOrder = (target?.dispatcherSortOrder ?? 0) - 0.5;
  } else {
    newOrder = Number.MAX_SAFE_INTEGER;
  }
  // Spread first: a cache can carry fields beside `jobs` (the completed list has `total`).
  return {
    ...cache,
    jobs: cache.jobs.map((j) =>
      j.id === vars.jobId
        ? { ...j, driverId: vars.assignedDriverId, dispatcherSortOrder: newOrder }
        : j,
    ),
  };
}

export function useAssignJob(): UseMutationResult<
  AssignResult,
  Error,
  AssignVars,
  MutationContext
> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useMutation<AssignResult, Error, AssignVars, MutationContext>({
    mutationFn: async (vars: AssignVars): Promise<AssignResult> => {
      const res = await authedFetch(opts, `/v1/jobs/${vars.jobId}/assignment`, {
        method: 'PATCH',
        body: JSON.stringify({
          assignedDriverId: vars.assignedDriverId,
          beforeJobId: vars.beforeJobId,
        }),
      });
      if (!res.ok) {
        throw await apiErrorFromResponse(res, 'assignment');
      }
      return res.json() as Promise<AssignResult>;
    },
    // Optimistic: update the cache synchronously so the board moves instantly.
    onMutate: (vars: AssignVars): MutationContext => {
      // Quick 260616-fxg #3 — snapshot + patch SYNCHRONOUSLY so the board moves in
      // the same frame as the drop (no flash / snap-back to the unassigned panel).
      // Cancelling in-flight refetches is fire-and-forget AFTER the optimistic write.
      const snapshot = qc.getQueriesData<JobsCache>({ queryKey: ['jobs'] });
      qc.setQueriesData<JobsCache>({ queryKey: ['jobs'] }, (old) =>
        isJobsCache(old) ? applyOptimistic(old, vars) : old,
      );
      void qc.cancelQueries({ queryKey: ['jobs'] });
      return { snapshot };
    },
    onError: (_err, _vars, ctx): void => {
      // Roll back every patched jobs cache to its pre-drop snapshot.
      ctx?.snapshot.forEach(([key, data]) => {
        qc.setQueryData(key, data);
      });
    },
    onSettled: (): void => {
      // Reconcile with the server's authoritative dense sort order.
      void qc.invalidateQueries({ queryKey: ['jobs'] });
    },
  });
}
